import React, { FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Field, withTypes } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@material-ui/core';
import { isEmpty, isEqual, merge } from 'lodash';
import { Button, Form } from '../../components';
import { ButtonVariant } from '../../components/Button/Button';
import AppContext from '../../context/AppContext';
import {
  DRAFT_ID,
  ListingPageFormIds,
  ListingPageParamTab,
  saveListingFlowIdToUrl,
  shouldDisableTradeIn,
} from './EditListingPage.utils';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { useRouteConfiguration } from '../../hooks/useRouteConfiguration';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { RequestStatus } from '../../types/requestStatus';
import { FindItemMethod, ListingItemType, OwnListing } from '../../types/sharetribe/listing';
import { ITEM_AVAILABILITY_AVAILABLE } from '../../util/constants';
import { createResourceLocatorString } from '../../util/routes';
import { getUrlHash, ListingPageParamType, parse } from '../../util/urlHelpers';
import { required } from '../../util/validators';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import ListAnyBrandToggle from './ListAnyBrandToggle';
import ListAnyBrandForm from './ListAnyBrandForm';
import { USER_GENERATED_BRAND_NAME } from './ItemBrandForm';
import { USER_GENERATED_TITLE } from './ItemNameForm';
import {
  buildSearchFormListingPayload,
  SearchFormListingPayload,
  SearchFormValues,
} from '../../util/listings/listingPayload';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import { CurrentListingType, useOwnListingVersions } from './hooks/useOwnListingVersions';
import { ProgressBarSection } from '../../types/shopConfig/shopConfigV2';
import SearchProgressBar from './ProgressBar/SearchProgressBar';
import { useCreditPayoutPercentage } from '../../hooks/useCreditPayoutPercentage';
import { useTradeInConfig } from '../../hooks/useTradeInConfig';
import { FIND_ITEM_METHOD } from '../../components/Shopify/Shopify.utils';
import {
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { useCashPayoutPercentage } from '../../hooks/useCashPayoutPercentage';
import CannotFindItem from './CannotFindItem';
import {
  createListingDraft,
  EditListingPageState,
  resetInitialState,
  updateListing,
  updateUnsavedListingValues,
} from './EditListingPage.duck';
import EditListingPageLayout from './EditListingPageLayout';
import FindWithOrderNumber from './FindWithOrderNumber';
import Search from './Search';
import { ListingFlowFormField } from './ListingFlowTypes';
import {
  getNewlyCompletedSectionNameToCompletionTime,
  trackCompletedListingSections,
} from './ProgressBar/ListingFlowProgressBar.utils';
import css from './EditListingPage.module.css';

const { Form: FinalForm } = withTypes<SearchFormValues>();

const SearchForm: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const routes = useRouteConfiguration();
  const { countryCode } = useBrandCountryConfig();
  const location = useLocation();
  const { listingItemType: listingItemTypeFromUrl } = parse(location.search);

  const currentUser = useSelector<any>((state) => state.user.currentUser) as
    | CurrentUser
    | undefined;
  const { params, updateListingStatus, fetchListingStatus } = useSelector<any>(
    (state) => state.EditListingPage
  ) as EditListingPageState;
  const { treetId } = useContext(AppContext);

  const { currentListing } = useOwnListingVersions();

  const { publicData } = currentListing.attributes;

  const shopConfig = useShopConfig();
  const { categoriesConfig, productTitleDisplayType } = useShopConfigV2();
  const { tradeInAllowedFindItemMethods } = useTradeInConfig();
  const { isBrand, isLoggedInAsLimitedUser, isTreet } = useCurrentUserPermissions();
  const { isListTradeInOnly, isMarketplaceOnly, allowTradeInAndMarketplace } =
    useEnabledCustomerExperiences();
  const { allowedFindItemMethods } = useListingFlowConfig();
  const cashPayoutPercentage = useCashPayoutPercentage();
  const creditPayoutPercentage = useCreditPayoutPercentage(
    publicData?.payoutInfo?.creditPayoutPercentage
  );
  const { shopName } = shopConfig;

  const ref = useRef(false);

  useEffect(() => {
    // Used for calculating listing create duration.
    const setUpNewListingFlowId = () => {
      const startTime = Date.now().valueOf();
      const listingFlowId = uuidv4();
      saveListingFlowIdToUrl(listingFlowId);
      dispatch(
        updateUnsavedListingValues({
          publicData: { listingStartTime: startTime, listingFlowId },
        })
      );
    };

    const hash = getUrlHash()?.substring(1);

    if (!hash) {
      ref.current = true;
      setUpNewListingFlowId();
      return;
    }
    const shopifyProductId = currentListing?.attributes?.publicData?.shopifyProductId;

    if (shopifyProductId && !ref.current) {
      ref.current = true;
      setUpNewListingFlowId();
    }
  }, [currentListing?.attributes?.publicData?.shopifyProductId]);

  useEffect(
    () => () => {
      if (params) {
        dispatch(
          resetInitialState({
            slug: params.slug,
            id: DRAFT_ID,
            type: params.type,
            tab: params.tab,
          })
        );
      }
    },
    []
  );

  const getUpdateValues = (values: SearchFormValues): SearchFormListingPayload => {
    const {
      shopifyProduct,
      categories,
      category,
      emailOrPhoneForOrder,
      lineItemId,
      orderConfirmationNumber,
      shopifyProductVariant,
      tags,
      userGeneratedTitle,
      selectedVariantOptions,
      findItemMethod,
      userGeneratedBrandName,
    } = values;

    const availability = publicData.availability as string | undefined;
    const configCategories = categoriesConfig?.categoriesCollection?.items || [];

    let itemTypeFromUrl;
    const isValidTradeIn =
      listingItemTypeFromUrl === ListingItemType.TradeIn &&
      !shouldDisableTradeIn(tradeInAllowedFindItemMethods, findItemMethod);
    const isValidItemType = Object.values(ListingItemType).includes(listingItemTypeFromUrl);
    if (
      listingItemTypeFromUrl &&
      isValidItemType &&
      (isValidTradeIn || listingItemTypeFromUrl === ListingItemType.Marketplace)
    ) {
      itemTypeFromUrl = listingItemTypeFromUrl;
    }

    const defaultListingItemType =
      (isListTradeInOnly && ListingItemType.TradeIn) ||
      (isMarketplaceOnly && ListingItemType.Marketplace) ||
      (allowTradeInAndMarketplace && undefined) ||
      itemTypeFromUrl;

    const originatedAsGuestListing = !currentUser;
    const listingStartTime = publicData?.listingStartTime;
    const listingFlowId = publicData?.listingFlowId;
    const disableAutoApproval = currentUser?.attributes.profile.publicData?.disableAutoApproval;

    const payload = buildSearchFormListingPayload({
      shopifyProduct,
      categories,
      category,
      emailOrPhoneForOrder,
      lineItemId,
      orderConfirmationNumber,
      shopifyProductVariant,
      tags,
      userGeneratedTitle,
      findItemMethod,
      userGeneratedBrandName,
      shopName,
      configCategories,
      productTitleDisplayType,
      treetId,
      cashPayoutPercentage,
      creditPayoutPercentage,
      selectedVariantOptions,
      availability: availability || ITEM_AVAILABILITY_AVAILABLE,
      isBrand: isBrand && !isTreet,
      disableAutoApproval,
      countryCode,
      defaultListingItemType,
      originatedAsGuestListing,
      listingStartTime,
      listingFlowId,
    });

    const newlyCompletedSectionTimes = getNewlyCompletedSectionNameToCompletionTime(
      [ProgressBarSection.FindItem],
      {}
    );
    payload.publicData.listingFlowSectionToTimestamp = newlyCompletedSectionTimes;

    return payload;
  };

  const shouldShowListAnythingToggle = allowedFindItemMethods.includes(FindItemMethod.ListAnyBrand);

  // TODO: extend to sanitize for every find item method
  const sanitizeValues = (values: SearchFormValues) => {
    if (
      shouldShowListAnythingToggle &&
      // filtering out cannot find since it also uses USER_GENERATED_TITLE
      values[FIND_ITEM_METHOD] !== FindItemMethod.CannotFind
    ) {
      // Ensures form does not submit with both User Generated Title/Brand
      // & Shopify Product, if both options are available.
      const fieldsToKeep =
        values[FIND_ITEM_METHOD] === FindItemMethod.ListAnyBrand
          ? [FIND_ITEM_METHOD, USER_GENERATED_BRAND_NAME, USER_GENERATED_TITLE]
          : [...Object.values(ListingFlowFormField), FIND_ITEM_METHOD];

      const sanitizedValues = Object.keys(values).reduce((acc: any, field: string) => {
        const updatedValue = fieldsToKeep.includes(field) ? (values as any)[field] : undefined;
        return { ...acc, [field]: updatedValue };
      }, {});

      return sanitizedValues as SearchFormValues;
    }
    return values;
  };

  // If the Shopify Product has changed, ensure that the
  // calculated values that depend on the Shopify Product
  // get reset to null so the Item Details Form does not have
  // stale product data.
  const getNullifiedListingAttributesForShopifyProduct = (
    existingListingValues: CurrentListingType,
    newValues: SearchFormListingPayload
  ) => {
    const { shopifyProductId, shopifyProductVariant } = newValues.publicData;
    const {
      shopifyProductId: currentListingShopifyProductId,
      shopifyProductVariant: currentListingShopifyProductVariant,
      selectedVariantOptions: currentListingSelectedVariantOptions,
    } = existingListingValues?.attributes?.publicData || {};

    const hasUpdatedShopifyProduct =
      currentListingShopifyProductId && shopifyProductId !== currentListingShopifyProductId;

    // Shopify Product Variant can update independently of Shopify Product in the event that
    // the newly selected Product already has a Variant associated with it (i.e. Order History).
    const hasUpdatedShopifyProductVariant =
      currentListingShopifyProductVariant?.id &&
      shopifyProductVariant?.id !== currentListingShopifyProductVariant?.id;

    if (
      !isEmpty(currentListingSelectedVariantOptions) &&
      (hasUpdatedShopifyProduct || hasUpdatedShopifyProductVariant)
    ) {
      const selectedValuesToClear = Object.keys(currentListingSelectedVariantOptions).reduce(
        (acc, option) => ({ ...acc, [option]: null }),
        {}
      );

      return {
        price: null,
        publicData: { ...selectedValuesToClear, stockPhotoUrls: null, originalPrice: null },
      };
    }

    return {};
  };

  const handleNextClick = async (values: SearchFormValues) => {
    setIsSubmitting(true);

    const sanitizedValues = sanitizeValues(values);
    const updateValues = getUpdateValues(sanitizedValues);
    const nullifiedShopifyProductValues = getNullifiedListingAttributesForShopifyProduct(
      currentListing,
      updateValues
    );
    const isNewURI = params?.type === ListingPageParamType.New;

    const {
      findItemMethod,
      listingFlowId,
      listingItemType,
      listingStartTime,
      listingFlowSectionToTimestamp: newlyCompletedSectionTimes,
    } = updateValues.publicData;

    // `shouldPreserveShopifyProductValues` check is to ensure CS can change the
    // underlying Shopify Product on a published listing without overwriting seller-approved values
    // by going to `/l/:slug/:id/edit/search`.
    const shouldPreserveShopifyProductValues =
      currentListing?.attributes?.state &&
      [LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_PUBLISHED].includes(
        currentListing?.attributes?.state
      );

    merge(updateValues, shouldPreserveShopifyProductValues ? {} : nullifiedShopifyProductValues);
    // if the price is null, createListingDraft will fail. instead, make sure its not in the payload
    if (updateValues.price === null) {
      delete updateValues.price;
    }

    if (listingStartTime && listingFlowId && newlyCompletedSectionTimes) {
      trackCompletedListingSections(listingStartTime, {}, newlyCompletedSectionTimes, {
        itemType: listingItemType,
        listingFlowId,
        findItemMethod,
      });
    }

    let listingDraft;

    if (currentUser) {
      if (isNewURI && !currentListing.id?.uuid) {
        listingDraft = (await dispatch(createListingDraft(updateValues))) as Partial<OwnListing>;
      } else {
        const upsertValues = { ...updateValues, id: currentListing.id?.uuid };
        listingDraft = (await dispatch(updateListing(upsertValues))) as Partial<OwnListing>;
      }
    }

    const currentPathParams = {
      ...params,
      type:
        params?.type === ListingPageParamType.Edit
          ? ListingPageParamType.Edit
          : ListingPageParamType.Draft,
      id: listingDraft?.id?.uuid || params?.id,
    };

    const hash = getUrlHash()?.substring(1);

    await dispatch(updateUnsavedListingValues(updateValues, true));
    const nextPathParams = { ...currentPathParams, tab: ListingPageParamTab.Details };
    const to = createResourceLocatorString('EditListingPage', routes, nextPathParams, {}, hash);

    // Replace current "new" path with "draft" path. Browser's back button should direct to
    // editing current draft instead of creating a new one, unless the user is not logged in.
    if (params?.type === ListingPageParamType.New && currentUser) {
      const draftURI = createResourceLocatorString(
        'EditListingPage',
        routes,
        currentPathParams,
        {},
        hash
      );
      history.replace(draftURI);
    }
    history.push(to);
  };

  let initialFindItemMethod: FindItemMethod;
  let body: ReactNode;
  switch (params?.tab) {
    case ListingPageParamTab.ListAnyBrand:
      initialFindItemMethod = FindItemMethod.ListAnyBrand;
      body = <ListAnyBrandForm />;
      break;
    case ListingPageParamTab.OrderNumber:
      initialFindItemMethod = FindItemMethod.OrderNumber;
      body = <FindWithOrderNumber />;
      break;
    case ListingPageParamTab.CannotFind:
      initialFindItemMethod = FindItemMethod.CannotFind;
      body = <CannotFindItem listingItemType={listingItemTypeFromUrl} />;
      break;
    case ListingPageParamTab.Search:
    default:
      initialFindItemMethod = FindItemMethod.Search;
      body = <Search shouldHideTitle={shouldShowListAnythingToggle} />;
      break;
  }

  const initialValues = {
    emailOrPhoneForOrder: publicData.emailOrPhoneForOrder || publicData.emailForOrder,
    orderConfirmationNumber: publicData.orderConfirmationNumber,
    userGeneratedTitle: publicData.userGeneratedTitle || publicData.shopifyProductName,
    userGeneratedBrandName: publicData.userGeneratedBrandName,
    findItemMethod: initialFindItemMethod,
    lineItemId: publicData.lineItemId,
    shopifyProduct: publicData.shopifyProduct,
    shopifyProductVariant: publicData.shopifyProductVariant,
  };

  const buildSubmitButton = (valid: boolean) => {
    const isLoading =
      updateListingStatus === RequestStatus.Pending || fetchListingStatus === RequestStatus.Pending;

    const listingState = currentListing?.attributes.state;
    const isUserEditingNonDraftListing =
      !isLoggedInAsLimitedUser && !!listingState && listingState !== LISTING_STATE_DRAFT;
    const isDisabled = !valid || isLoading || isUserEditingNonDraftListing;

    return (
      <Button
        variant={ButtonVariant.Primary}
        disabled={isDisabled}
        inProgress={isLoading}
        className={css.footerButton}
        type="submit"
        form={ListingPageFormIds.Search}
      >
        Next: Item Details
      </Button>
    );
  };

  return (
    <>
      <FinalForm
        initialValuesEqual={(prevInitialValues, currInitialValues) => {
          if (!currInitialValues) return false;
          // if is submitting, don't reinitialize
          if (isSubmitting) return true;
          // RFF's default initialValuesEqual only does a shallow comparison of fields. Here we need
          // to do a deep comparison to check for matching nested object fields, e.g. images.
          return isEqual(prevInitialValues, currInitialValues);
        }}
        onSubmit={handleNextClick}
        initialValues={initialValues}
        render={(formRenderProps) => {
          const { handleSubmit, values, valid } = formRenderProps;
          return (
            <Form id={ListingPageFormIds.Search} onSubmit={handleSubmit}>
              <EditListingPageLayout
                progressBar={<SearchProgressBar />}
                submitButton={buildSubmitButton(valid)}
              >
                {values.findItemMethod !== FindItemMethod.CannotFind &&
                  values.findItemMethod !== FindItemMethod.ListAnyBrand && (
                    <Field
                      id="shopifyProduct"
                      name="shopifyProduct"
                      input={<input type="hidden" />}
                      validate={required('Please complete the steps.')}
                      hidden
                      component="input"
                    />
                  )}
                <Box pb={1} />
                {shouldShowListAnythingToggle && <ListAnyBrandToggle />}
                <Box pb={3}>{body}</Box>
              </EditListingPageLayout>
            </Form>
          );
        }}
      />
    </>
  );
};

export default SearchForm;
